import { Button } from "@/components/ui/Button";
import type { CurationCellStatus, CurationColumn } from "@/features/data-curation/types";

const STATUSES: { value: CurationCellStatus; label: string }[] = [
  { value: "explained", label: "Explained" },
  { value: "rejected", label: "Rejected" },
  { value: "pending", label: "Pending" },
];

const FILTER_SELECT =
  "h-8 max-w-48 rounded-[3px] border border-zinc-300 bg-white px-2 text-xs font-normal text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900";

interface GridFiltersProps {
  status: CurationCellStatus | "";
  columnId: string;
  columns: CurationColumn[];
  /** The rows that match the filters; null while they load. */
  matching: number | null;
  onStatusChange: (status: CurationCellStatus | "") => void;
  onColumnChange: (columnId: string) => void;
  onClear: () => void;
}

/** The status and column filters of the grid, on the line of the legend. */
export const GridFilters = ({
  status,
  columnId,
  columns,
  matching,
  onStatusChange,
  onColumnChange,
  onClear,
}: GridFiltersProps) => {
  const active = status !== "" || columnId !== "";
  return (
    <div role="group" aria-label="Grid filters" className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
        Status
        <select
          value={status}
          onChange={(event) => onStatusChange(event.target.value as CurationCellStatus | "")}
          className={FILTER_SELECT}
        >
          <option value="">All statuses</option>
          {STATUSES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600">
        Column
        <select
          value={columnId}
          onChange={(event) => onColumnChange(event.target.value)}
          className={FILTER_SELECT}
        >
          <option value="">All columns</option>
          {columns.map((column) => (
            <option key={column.column_id} value={column.column_id}>
              {column.label}
            </option>
          ))}
        </select>
      </label>
      {active && (
        <>
          <span className="text-xs tabular-nums text-zinc-500" aria-live="polite">
            {matching == null
              ? "Filtering…"
              : `${matching} matching ${matching === 1 ? "row" : "rows"}`}
          </span>
          <Button variant="outline" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        </>
      )}
    </div>
  );
};
